import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/Button";
import { useParcelDataEnrichment, useTaskPolling } from "../../hooks";
import { fmtDateTime } from "../../utils";
import type { MpzpProjectSummary } from "../../types";

/**
 * Pasek braków nad listą działek — JEDEN WIERSZ NA PROJEKT.
 *
 * Przeliczenia idą per projekt, bo jedno zapytanie do usługi mapowej obsługuje wszystkie
 * jego działki naraz. Gdyby przycisk działał na grupie gminnej, jedno kliknięcie
 * odpalałoby zadania na wszystkich projektach, które mają tam działki — łącznie z tymi,
 * których użytkownik wcale nie oglądał.
 *
 * „Przelicz ponownie" odpala wszystko, czego brakuje, RÓWNOLEGLE. Obok stoją daty
 * ostatniego sprawdzenia, żeby nie przeliczać w kółko tego samego — usługi GUGiK i GDOŚ
 * są wolne, a wynik zmienia się rzadko.
 */

type Job = "admin" | "streets" | "mpzp" | "gdos";

const JOB_LABELS: Record<Job, string> = {
  admin: "dane administracyjne",
  streets: "adresy",
  mpzp: "pokrycie MPZP",
  gdos: "tereny chronione",
};

/** Pojedyncze zadanie w tle — własny komponent, bo useTaskPolling to hook. */
function TaskWatch({
  taskId,
  label,
  onSettled,
}: {
  taskId: string;
  label: string;
  onSettled: (id: string, ok: boolean) => void;
}) {
  const { data } = useTaskPolling(taskId);
  const status = data?.status;

  useEffect(() => {
    if (!status || !["success", "failure", "cancelled"].includes(status)) return;
    onSettled(taskId, status === "success");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const done = status === "success";
  const failed = status === "failure" || status === "cancelled";
  return (
    <span
      className={
        "px-1.5 py-0.5 text-[10px] font-mono uppercase border " +
        (done
          ? "bg-green-50 text-green-800 border-green-300"
          : failed
            ? "bg-red-50 text-red-800 border-red-300"
            : "bg-[var(--color-bg-surface)] text-[var(--color-text-muted)] border-[var(--color-border)]")
      }
      title={failed ? data?.error || "nie powiodło się" : undefined}
    >
      {label}
      {done ? " ✓" : failed ? " ✗" : " …"}
    </span>
  );
}

function Stamp({ label, at }: { label: string; at: string | null }) {
  return (
    <span className="inline-flex items-baseline gap-1 text-[10px] font-mono text-[var(--color-text-muted)]">
      <span className="uppercase tracking-wide">{label}</span>
      {at ? (
        <span className="text-[var(--color-text-secondary)]" title={fmtDateTime(at)}>
          {fmtDateTime(at)}
        </span>
      ) : (
        <span className="italic">nigdy</span>
      )}
    </span>
  );
}

/** Pojedynczy brak — chip, żeby nie zlewało się w jedno zdanie oddzielane kropkami. */
function GapChip({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wide border border-amber-300 bg-amber-50 text-amber-800">
      {children}
    </span>
  );
}

function ProjectRow({
  project,
  onDone,
}: {
  project: MpzpProjectSummary;
  onDone: () => void;
}) {
  const enrich = useParcelDataEnrichment();
  const [tasks, setTasks] = useState<{ id: string; label: string }[]>([]);
  const [settled, setSettled] = useState<Record<string, boolean>>({});

  const gaps: string[] = [];
  if (project.missing_admin) gaps.push(`${project.missing_admin} bez gminy/obrębu`);
  if (project.missing_address) gaps.push(`${project.missing_address} bez miejscowości i adresu`);
  if (project.unchecked_mpzp) gaps.push(`${project.unchecked_mpzp} bez pokrycia MPZP`);
  if (project.unchecked_gdos) gaps.push(`${project.unchecked_gdos} bez terenów chronionych`);

  const missingTotal = Math.max(
    project.missing_admin,
    project.missing_address,
    project.unchecked_mpzp,
    project.unchecked_gdos,
  );
  // Czy cokolwiek w tym projekcie było już liczone — od tego zależy, czy „uzupełnij
  // braki" i „przelicz od nowa" w ogóle się różnią.
  const anythingChecked = Object.values(project.last_checked).some(Boolean);

  const running = tasks.length > 0 && Object.keys(settled).length < tasks.length;

  const onSettled = (id: string, ok: boolean) =>
    setSettled((s) => (id in s ? s : { ...s, [id]: ok }));

  useEffect(() => {
    if (!tasks.length || Object.keys(settled).length < tasks.length) return;
    setTasks([]);
    setSettled({});
    onDone();
    enrich.invalidate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settled, tasks.length]);

  /** Odpala RÓWNOLEGLE wszystko, czego brakuje. `force` przelicza także to, co już jest. */
  const recompute = async (force: boolean) => {
    if (running) return;
    const id = project.connection_id;
    const jobs: { job: Job; run: () => Promise<{ task_id: string }> }[] = [];
    if (force || project.missing_admin) {
      jobs.push({ job: "admin", run: () => enrich.adminData.mutateAsync(id) });
    }
    if (force || project.missing_address) {
      jobs.push({
        job: "streets",
        run: () => enrich.streets.mutateAsync({ connectionId: id, force }),
      });
    }
    if (force || project.unchecked_mpzp) {
      jobs.push({
        job: "mpzp",
        run: () => enrich.mpzpCoverage.mutateAsync({ connectionId: id, force }),
      });
    }
    if (force || project.unchecked_gdos) {
      jobs.push({ job: "gdos", run: () => enrich.protectedAreas.mutateAsync(id) });
    }
    if (!jobs.length) return;

    const started = await Promise.all(
      jobs.map(async (j) => {
        try {
          const { task_id } = await j.run();
          return { id: task_id, label: JOB_LABELS[j.job] };
        } catch {
          return null;
        }
      }),
    );
    const ok = started.filter(Boolean) as { id: string; label: string }[];
    setSettled({});
    setTasks(ok);
    if (!ok.length) onDone();
  };

  const nothingMissing = !gaps.length;

  return (
    <div className="border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-2.5 py-2 space-y-2">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 space-y-1.5">
          <div className="flex items-baseline gap-2 min-w-0">
            <Link
              to={`/power-projects/${project.project_id}?tab=parcels`}
              className="text-xs font-medium leading-snug line-clamp-2 max-w-[34rem] hover:text-[var(--color-primary)] hover:underline"
              title={project.project_name}
            >
              {project.project_name}
            </Link>
            <span className="text-[10px] font-mono uppercase tracking-wide text-[var(--color-text-muted)] shrink-0">
              {project.parcel_count} dz.
            </span>
          </div>
          {nothingMissing ? (
            <div className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wide border border-green-300 bg-green-50 text-green-800">
              Komplet danych
            </div>
          ) : (
            <div className="flex flex-wrap gap-1">
              {gaps.map((g) => (
                <GapChip key={g}>{g}</GapChip>
              ))}
            </div>
          )}
          <div className="flex flex-wrap gap-x-3 gap-y-0.5">
            <Stamp label="adresy" at={project.last_checked.address} />
            <Stamp label="MPZP" at={project.last_checked.mpzp} />
            <Stamp label="GDOŚ" at={project.last_checked.gdos} />
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          {/* Dopóki nic nie było liczone, „uzupełnij braki" i „przelicz od nowa"
              robią DOKŁADNIE to samo — dwa przyciski o tym samym działaniu tylko
              każą zgadywać, więc pokazujemy jeden. Rozjeżdżają się dopiero, gdy
              część danych już jest. */}
          {!anythingChecked ? (
            <Button
              size="sm"
              variant="secondary"
              loading={running}
              disabled={nothingMissing}
              onClick={() => recompute(false)}
            >
              Przelicz dane działek
            </Button>
          ) : (
            <>
              {!nothingMissing && (
                <Button
                  size="sm"
                  variant="secondary"
                  loading={running}
                  onClick={() => recompute(false)}
                  title="Odpytuje usługi TYLKO dla działek, którym brakuje danych — szybsze"
                >
                  Uzupełnij braki ({missingTotal})
                </Button>
              )}
              <Button
                size="sm"
                variant="ghost"
                disabled={running}
                onClick={() => recompute(true)}
                title="Odpytuje wszystkie usługi od nowa, także dla działek już sprawdzonych — wolniejsze, ale łapie zmiany (nowy plan miejscowy, nowy punkt adresowy)"
              >
                Przelicz wszystko od nowa
              </Button>
            </>
          )}
        </div>
      </div>
      {/* Różnica między przyciskami napisana wprost — tooltip widzi tylko ten, kto
          już podejrzewa, że różnica istnieje. */}
      {!running && (
        <div className="text-[11px] leading-relaxed text-[var(--color-text-muted)] border-t border-[var(--color-border)] pt-1.5">
          {!anythingChecked ? (
            <>
              Nic jeszcze nie było liczone dla tego projektu — jedno kliknięcie odpyta
              równolegle wszystkie potrzebne usługi (rejestr działek, geokoder, plany
              miejscowe, GDOŚ).
            </>
          ) : (
            <>
              <b>Uzupełnij braki</b> — pyta tylko o działki, którym czegoś brakuje (szybsze).{" "}
              <b>Przelicz wszystko od nowa</b> — odpytuje też działki już sprawdzone, żeby
              złapać zmiany (nowy plan miejscowy, nowy punkt adresowy). Wolniejsze, więc rób
              to tylko wtedy, gdy daty wyżej są stare.
            </>
          )}
        </div>
      )}
      {tasks.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 pt-1">
          <span className="text-[11px] text-[var(--color-text-muted)]">
            {running ? "Przeliczam w tle:" : "Gotowe:"}
          </span>
          {tasks.map((t) => (
            <TaskWatch key={t.id} taskId={t.id} label={t.label} onSettled={onSettled} />
          ))}
        </div>
      )}
    </div>
  );
}

export function MpzpDataGapsBar({
  projects,
  onDone,
}: {
  projects: MpzpProjectSummary[];
  onDone: () => void;
}) {
  if (!projects.length) return null;
  return (
    <div className="space-y-2">
      <div className="section-label">Dane działek — przeliczanie per projekt</div>
      {projects.map((p) => (
        <ProjectRow key={p.connection_id} project={p} onDone={onDone} />
      ))}
    </div>
  );
}
